import React, { useEffect } from 'react';
import { CustomDialog } from '../../Component/Dialog';
import {
  Select,
  MenuItem,
  ButtonGroup,
  TextField as MuiTextfield,
  Chip as MuiChip,
  Checkbox,
  RadioGroup,
  Radio,
  FormControlLabel,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Box,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import { Autocomplete } from '@mui/lab';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import './FormDialogs.css';
import useCanvasStore from '../../pages/Canvas/useCanvasStore';
import ViewListIcon from '@mui/icons-material/ViewList';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

// URL validation helpers
const isValidUrl = url => {
  if (!url || url.trim() === '') return false;
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};

const ensureUrlProtocol = url => {
  if (!url || url.trim() === '') return url;
  const trimmedUrl = url.trim();
  // If URL already has a valid protocol, return as-is
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }
  // Auto-prepend https://
  return `https://${trimmedUrl}`;
};

export const FormSettingsDialog = props => {
  const {
    isForm,
    linkedComponents,
    linkedComponent,
    componentId,
    relatedSequentialForm,
    loading,
    redirectToExternalUrl,
    redirectUrl,
  } = props;

  const redirectFormOptionsExcludingSelf = relatedSequentialForm.filter(
    formComponent => formComponent.componentId !== componentId
  );

  // Determine the current submission action based on state
  const getSubmissionAction = () => {
    if (redirectToExternalUrl) return 'externalUrl';
    if (linkedComponent) return 'linkedForm';
    return 'submission';
  };

  const handleSubmissionActionChange = value => {
    if (value === 'submission') {
      props.setLinkedComponent(false);
      props.setRedirectToExternalUrl(false);
    } else if (value === 'linkedForm') {
      props.setLinkedComponent(true);
      props.setRedirectToExternalUrl(false);
    } else if (value === 'externalUrl') {
      props.setLinkedComponent(false);
      props.setRedirectToExternalUrl(true);
    }
  };

  useEffect(() => {
    if (!Array.isArray(linkedComponents)) return;
    const isValidLinkedComponents = linkedComponents.every(
      component =>
        component &&
        typeof component.componentId === 'string' &&
        component.componentId.trim() !== ''
    );

    if (linkedComponent === true && isValidLinkedComponents) {
      useCanvasStore.getState().setFormRedirectEdges(componentId, linkedComponents);
    } else if (linkedComponent === false && !loading) {
      useCanvasStore.getState().setFormRedirectEdges(componentId, []);
    }
  }, [linkedComponents, linkedComponent, loading]);

  return (
    <CustomDialog
      isOpen={props.openSettings}
      onClose={() => {
        props.setOpenSettings(false);
      }}
      title={'Form settings'}
      contents={
        <>
          <p className={'stand-alone-text'}>Form display mode</p>
          <p className={'s-text'}>Choose how questions are displayed to users</p>
          <ToggleButtonGroup
            value={props.displayMode || 'classic'}
            exclusive
            onChange={(e, value) => value && props.setDisplayMode(value)}
            className="form-display-mode-selector"
          >
            <ToggleButton value="classic" className="form-display-mode-btn">
              <ViewListIcon />
              <Box className="form-display-mode-text">
                <span className="form-display-mode-label">Classic</span>
                <span className="form-display-mode-desc">All questions on page</span>
              </Box>
            </ToggleButton>
            <ToggleButton value="conversational" className="form-display-mode-btn">
              <ChatBubbleOutlineIcon />
              <Box className="form-display-mode-text">
                <span className="form-display-mode-label">Conversational</span>
                <span className="form-display-mode-desc">One question at a time</span>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
          <Divider sx={{ my: 2 }} />
          <p className={'stand-alone-text'}>After this form is submitted</p>
          <p className={'s-text'}>Select what happens when this form is submitted </p>
          <RadioGroup
            onChange={e => {
              handleSubmissionActionChange(e.target.value);
            }}
            value={getSubmissionAction()}
          >
            <FormControlLabel
              value="submission"
              control={<Radio />}
              label="display submission page"
            />
            <FormControlLabel
              value="linkedForm"
              control={<Radio />}
              label="redirect to another form"
            />
            <FormControlLabel
              value="externalUrl"
              control={<Radio />}
              label="redirect to external URL"
            />
          </RadioGroup>

          {linkedComponent && (
            <>
              <Autocomplete
                style={{ marginTop: '8px' }}
                options={redirectFormOptionsExcludingSelf}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <MuiChip
                      key={index}
                      className="form-chip"
                      label={option.label}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                size={'small'}
                multiple
                freeSolo={false}
                openOnFocus={true}
                getOptionLabel={option => {
                  return option.label;
                }}
                variant={'outlined'}
                value={linkedComponents}
                blurOnSelect={false}
                className={'form-autocomplete'}
                onChange={(event, inputValue, reason) => {
                  if (reason == 'clear') {
                    props.setLinkedComponents([]);
                  } else {
                    props.setLinkedComponents(inputValue);
                  }
                }}
                renderInput={params => {
                  return (
                    <MuiTextfield
                      {...params}
                      placeholder={'Select a connected task or form section'}
                      size={'small'}
                      variant="outlined"
                      error={linkedComponents.length === 0}
                      FormHelperTextProps={{
                        className: 'form-helper-text',
                      }}
                      helperText={
                        linkedComponents.length === 0 &&
                        'Select a form to redirect to when this form is submitted'
                      }
                    />
                  );
                }}
              />
            </>
          )}

          {redirectToExternalUrl && (
            <>
              <MuiTextfield
                style={{ marginTop: '8px' }}
                fullWidth
                size="small"
                variant="outlined"
                placeholder="https://example.com"
                value={redirectUrl || ''}
                onChange={e => props.setRedirectUrl(e.target.value)}
                onBlur={e => {
                  // Auto-prepend https:// if user didn't include a protocol
                  const normalizedUrl = ensureUrlProtocol(e.target.value);
                  if (normalizedUrl !== e.target.value) {
                    props.setRedirectUrl(normalizedUrl);
                  }
                }}
                error={!redirectUrl || redirectUrl.trim() === '' || !isValidUrl(redirectUrl)}
                helperText={
                  !redirectUrl || redirectUrl.trim() === ''
                    ? 'Enter the URL to redirect to after form submission'
                    : !isValidUrl(redirectUrl)
                    ? 'Please enter a valid URL (e.g., https://example.com)'
                    : ''
                }
                FormHelperTextProps={{
                  className: 'form-helper-text',
                }}
              />
            </>
          )}

          {!props.linkedComponent && !redirectToExternalUrl && (
            <>
              <p className={'primary-text'}>Submission Message</p>
              <p className={'s-text'}>
                Specify the message displayed after a submission has been made.{' '}
              </p>
              <TextField
                variant="outlined"
                multiline
                fullWidth
                rows={3}
                onChange={event => {
                  props.setSubmMsg(event.target.value);
                }}
                value={props.submMsg}
                defaultValue={'Thank you for your submission. Your response has been recorded.'}
                placeholder="Type in a message to be displayed when the user submits the form"
              />
            </>
          )}
          <Divider sx={{ my: 2 }} />
          <p className={'stand-alone-text'}>
            Allow the user to view, edit and resubmit their response
          </p>
          <ButtonGroup>
            <Button
              id={'ON'}
              value={true}
              className="d-button"
              color={props.canEditSubmission === true && !props.canReadOnly ? 'primary' : 'info'}
              variant={
                props.canEditSubmission === true && !props.canReadOnly ? 'contained' : 'outlined'
              }
              onClick={() => {
                props.setCanEditSubmission(true);
                props.setCanReadOnly(false);
              }}
            >
              On
            </Button>
            <Button
              id={'READ'}
              value={true}
              className="d-btn-read-only"
              color={props.canReadOnly === true ? 'primary' : 'info'}
              variant={props.canReadOnly === true ? 'contained' : 'outlined'}
              onClick={() => {
                props.setCanEditSubmission(false);
                props.setCanReadOnly(true);
              }}
            >
              Read Only
            </Button>
            <Button
              id={'OFF'}
              value={false}
              className="d-button"
              color={props.canEditSubmission === false && !props.canReadOnly ? 'primary' : 'info'}
              variant={
                props.canEditSubmission === false && !props.canReadOnly ? 'contained' : 'outlined'
              }
              onClick={() => {
                props.setCanEditSubmission(false);
                props.setCanReadOnly(false);
              }}
            >
              Off
            </Button>
          </ButtonGroup>
          {isForm ? (
            <>
              <p className={'stand-alone-text'}>
                Allow user to start a new submission after finishing the form
              </p>
              <ButtonGroup>
                <Button
                  className="d-button"
                  color={props.canAddSubmission ? 'primary' : 'info'}
                  variant={props.canAddSubmission ? 'contained' : 'outlined'}
                  onClick={() => props.setCanAddSubmission(true)}
                >
                  On
                </Button>
                <Button
                  className="d-button"
                  color={props.canAddSubmission ? 'info' : 'primary'}
                  variant={props.canAddSubmission ? 'outlined' : 'contained'}
                  onClick={() => props.setCanAddSubmission(false)}
                >
                  Off
                </Button>
              </ButtonGroup>
              <p className={'stand-alone-text'}>Allow user to save their response</p>
              <ButtonGroup>
                <Button
                  className="d-button"
                  color={props.canSaveSubmission ? 'primary' : 'info'}
                  variant={props.canSaveSubmission ? 'contained' : 'outlined'}
                  onClick={() => props.setCanSaveSubmission(true)}
                >
                  On
                </Button>
                <Button
                  className="d-button"
                  color={props.canSaveSubmission ? 'info' : 'primary'}
                  variant={props.canSaveSubmission ? 'outlined' : 'contained'}
                  onClick={() => {
                    props.setCanSaveSubmission(false);
                    props.setAutoSave(false);
                  }}
                >
                  Off
                </Button>
              </ButtonGroup>
            </>
          ) : (
            <>
              <p className={'stand-alone-text'}>Allow user to save their response</p>
              <ButtonGroup>
                <Button
                  className="d-button"
                  color={props.canSaveSubmission ? 'primary' : 'info'}
                  variant={props.canSaveSubmission ? 'contained' : 'outlined'}
                  onClick={() => props.setCanSaveSubmission(true)}
                >
                  On
                </Button>
                <Button
                  className="d-button"
                  color={props.canSaveSubmission ? 'info' : 'primary'}
                  variant={props.canSaveSubmission ? 'outlined' : 'contained'}
                  onClick={() => {
                    props.setCanSaveSubmission(false);
                    props.setAutoSave(false);
                  }}
                >
                  Off
                </Button>
              </ButtonGroup>
            </>
          )}
          {props.canSaveSubmission && (
            <div className="autosaveGrid">
              <Checkbox
                checked={props.autoSave}
                onClick={() => props.setAutoSave(!props.autoSave)}
                className="notify-email-checkbox"
                size="medium"
              />
              <span className="comment-email-text">Auto-save form every 30 seconds</span>
            </div>
          )}
          <p className={'stand-alone-text'}>Allow user to submit their response</p>
          <ButtonGroup>
            <Button
              className="d-button"
              color={props.canSubmitSubmission ? 'primary' : 'info'}
              variant={props.canSubmitSubmission ? 'contained' : 'outlined'}
              onClick={() => props.setCanSubmitSubmission(true)}
            >
              On
            </Button>
            <Button
              className="d-button"
              color={props.canSubmitSubmission ? 'info' : 'primary'}
              variant={props.canSubmitSubmission ? 'outlined' : 'contained'}
              onClick={() => props.setCanSubmitSubmission(false)}
            >
              Off
            </Button>
          </ButtonGroup>
        </>
      }
      buttons={
        <>
          <Button onClick={props.handleCancelSettings} variant={'outlined'} color={'info'}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (linkedComponent && linkedComponents.length === 0) return;
              if (
                redirectToExternalUrl &&
                (!redirectUrl || redirectUrl.trim() === '' || !isValidUrl(redirectUrl))
              )
                return;
              props.setOpenSettings(false);
            }}
            variant={'contained'}
            color={'primary'}
          >
            Done
          </Button>
        </>
      }
    />
  );
};

FormSettingsDialog.propTypes = {
  openSettings: PropTypes.bool.isRequired,
  linkedComponent: PropTypes.bool.isRequired,
  setLinkedComponent: PropTypes.func.isRequired,
  setOpenSettings: PropTypes.func.isRequired,
  relatedSequentialForm: PropTypes.array.isRequired,
  linkedComponents: PropTypes.array.isRequired,
  setLinkedComponents: PropTypes.func.isRequired,
  submMsg: PropTypes.string.isRequired,
  setSubmMsg: PropTypes.func.isRequired,
  canEditSubmission: PropTypes.bool,
  setCanEditSubmission: PropTypes.func.isRequired,
  canReadOnly: PropTypes.bool,
  setCanReadOnly: PropTypes.func.isRequired,
  canAddSubmission: PropTypes.bool.isRequired,
  setCanAddSubmission: PropTypes.func.isRequired,
  canSaveSubmission: PropTypes.bool.isRequired,
  setCanSaveSubmission: PropTypes.func.isRequired,
  autoSave: PropTypes.bool.isRequired,
  setAutoSave: PropTypes.func.isRequired,
  displayMode: PropTypes.string,
  setDisplayMode: PropTypes.func.isRequired,
  canAnnotateContent: PropTypes.bool.isRequired,
  setCanAnnotateContent: PropTypes.func.isRequired,
  canSubmitSubmission: PropTypes.bool.isRequired,
  isForm: PropTypes.bool,
  setCanSubmitSubmission: PropTypes.func.isRequired,
  handleCancelSettings: PropTypes.func.isRequired,
  redirectToExternalUrl: PropTypes.bool,
  setRedirectToExternalUrl: PropTypes.func,
  redirectUrl: PropTypes.string,
  setRedirectUrl: PropTypes.func,
};
